import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2,
  CalendarRange,
  ListFilter,
  Loader2,
  MoreHorizontal,
  Plus,
  Search,
  Users2,
} from 'lucide-react';
import moment from 'moment';

import {
  useAdminOrganizationLookupQuery,
  useAdminOrganizationsQuery,
  useCreateOrganizationMutation,
  useOrganizationLifecycleMutation,
  type OrgLifecycleAction,
} from '@/client/api/admin/organizations';
import type {
  AdminOrganizationCreatePayload,
  AdminOrganizationListRow,
  AdminOrganizationSearchOrg,
  AdminOrganizationsListParams,
} from '@/client/types/admin/users-orgs';

import SearchableSelect from '@/client/components/admin/SearchableSelect';
import OrgSearchSelect from '@/client/components/common/OrgSearchSelect';
import StatsGrid from '@/client/components/common/StatsGrid';
import Pagination from '@/client/components/common/Pagination';
import SortableTh from '@/client/components/common/SortableTh';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useDebouncedSearch } from '@/client/hooks/useDebouncedSearch';
import { getDateRange, type Period } from '@/client/utils/filters';
import { OrgStateBadges } from './OrgStateBadges';
import { buildOrganizationStats } from './OrganizationList/stats';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

/** Re-export for detail / moderation pages that imported from the list file. */
export { OrgStateBadges } from './OrgStateBadges';

const SIZE_OPTIONS = [
  { value: '', label: 'Any size' },
  { value: '1-10', label: 'Small (1–10)' },
  { value: '11-50', label: 'Medium (11–50)' },
  { value: '51-200', label: 'Large (51–200)' },
  { value: '201-1000000', label: 'Enterprise (201+)' },
];

const LISTING_OPTIONS = [
  { value: '', label: 'Any listings' },
  { value: '0', label: 'None (0)' },
  { value: '1-10', label: '1–10' },
  { value: '11-50', label: '11–50' },
  { value: '51-200', label: '51–200' },
  { value: '201-1000000', label: '201+' },
];

const MEMBERS_OPTIONS = [
  { value: '', label: 'Any members' },
  { value: '0', label: 'None (0)' },
  { value: '1-10', label: '1–10' },
  { value: '11-50', label: '11–50' },
  { value: '51-200', label: '51–200' },
  { value: '201-1000000', label: '201+' },
];

const PERIOD_OPTIONS = [
  { value: '', label: 'Created any time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'lastYear', label: 'Last Year' },
];

const LIFECYCLE_COPY: Record<
  OrgLifecycleAction,
  { label: string; title: string; body: string; destructive: boolean }
> = {
  activate: {
    label: 'Activate',
    title: 'Activate organization?',
    body: 'The organization becomes active again. This does not change its review or hold state.',
    destructive: false,
  },
  deactivate: {
    label: 'Deactivate',
    title: 'Deactivate organization?',
    body: 'The organization is switched off. Members lose access until it is reactivated. This does not change its review or hold state.',
    destructive: true,
  },
  delete: {
    label: 'Delete',
    title: 'Soft-delete organization?',
    body: 'This is a soft delete — the record is retained and can be restored with Undelete.',
    destructive: true,
  },
  undelete: {
    label: 'Undelete',
    title: 'Restore organization?',
    body: 'The organization record is restored. Note that member org-roles dropped at delete time are NOT restored and must be re-assigned.',
    destructive: false,
  },
};

const EMPTY_CREATE: AdminOrganizationCreatePayload = {
  name: '',
  description: '',
  country: '',
  locale: '',
};

const OrganizationListPage = () => {
  useBreadcrumbs([{ label: 'Organizations' }]);

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const {
    value: search,
    setValue: setSearch,
    debounced: debouncedSearch,
    isDebouncing,
  } = useDebouncedSearch('', { delay: 400 });
  const [sortBy, setSortBy] = useState('oid');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [size, setSize] = useState('');
  const [listings, setListings] = useState('');
  const [members, setMembers] = useState('');
  const [period, setPeriod] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [onlyReview, setOnlyReview] = useState(false);
  const [onlyHeld, setOnlyHeld] = useState(false);
  const [onlyUndiscoverable, setOnlyUndiscoverable] = useState(false);
  const [onlyOrphans, setOnlyOrphans] = useState(false);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [onlyVat, setOnlyVat] = useState(false);
  /** `''` = all, `'0'` = real only, `'1'` = testers only. */
  const [testerFilter, setTesterFilter] = useState<'' | '0' | '1'>('');
  const [orgJumpQuery, setOrgJumpQuery] = useState('');
  const [ownerEmailInput, setOwnerEmailInput] = useState('');
  const [ownerEmailLookup, setOwnerEmailLookup] = useState<string | null>(null);

  const resetPage = () => setPage(1);

  const ownerLookup = useAdminOrganizationLookupQuery(
    ownerEmailLookup ? { owner_email: ownerEmailLookup } : null,
    Boolean(ownerEmailLookup)
  );

  useEffect(() => {
    if (!ownerEmailLookup || ownerLookup.isFetching) return;
    if (ownerLookup.data?.oid != null) {
      navigate(`/admin/organizations/${ownerLookup.data.oid}`);
      setOwnerEmailLookup(null);
      setOwnerEmailInput('');
      return;
    }
    if (ownerLookup.isError) {
      toast.error('No organization found for that owner email', {
        description: ownerEmailLookup,
        richColors: true,
      });
      setOwnerEmailLookup(null);
    }
  }, [
    ownerEmailLookup,
    ownerLookup.data,
    ownerLookup.isError,
    ownerLookup.isFetching,
    navigate,
  ]);

  const params = useMemo<AdminOrganizationsListParams>(() => {
    const next: AdminOrganizationsListParams = {
      page,
      page_size: limit,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
    if (debouncedSearch) next.search = debouncedSearch;
    if (size) next.size = size;
    if (listings) next.listings = listings;
    if (members) next.members = members;
    if (period) Object.assign(next, getDateRange(period as Period));
    if (includeDeleted) next.deleted = 1;
    if (onlyReview) next.review = 1;
    if (onlyHeld) next.admin_hold = 1;
    if (onlyUndiscoverable) next.searchable = 0;
    if (onlyOrphans) next.has_owner = 0;
    if (onlyVerified) next.verified = 1;
    if (onlyVat) next.vat_registered = 1;
    if (testerFilter === '0' || testerFilter === '1') {
      next.tester = Number(testerFilter) as 0 | 1;
    }
    return next;
  }, [
    page,
    limit,
    sortBy,
    sortOrder,
    debouncedSearch,
    size,
    listings,
    members,
    period,
    includeDeleted,
    onlyReview,
    onlyHeld,
    onlyUndiscoverable,
    onlyOrphans,
    onlyVerified,
    onlyVat,
    testerFilter,
  ]);

  const { data, isLoading, isFetching, isError, error } =
    useAdminOrganizationsQuery(params);
  const organizations = data?.organizations ?? [];
  const summary = data?.organization_summary;
  const orgStats = useMemo(() => buildOrganizationStats(summary), [summary]);

  const activeStatKeys = useMemo(() => {
    const keys: string[] = [];
    if (onlyOrphans) keys.push('orphans');
    if (testerFilter === '1') keys.push('testers');
    if (onlyHeld) keys.push('held');
    if (onlyReview) keys.push('review');
    if (onlyUndiscoverable) keys.push('undiscoverable');
    if (includeDeleted) keys.push('deleted');
    if (onlyVerified) keys.push('verified');
    if (onlyVat) keys.push('vat');
    if (
      !onlyOrphans &&
      !testerFilter &&
      !onlyHeld &&
      !onlyReview &&
      !onlyUndiscoverable &&
      !includeDeleted &&
      !onlyVerified &&
      !onlyVat
    ) {
      keys.push('total');
    }
    return keys;
  }, [
    onlyOrphans,
    testerFilter,
    onlyHeld,
    onlyReview,
    onlyUndiscoverable,
    includeDeleted,
    onlyVerified,
    onlyVat,
  ]);

  const handleStatClick = (key: string) => {
    switch (key) {
      case 'total':
        setTesterFilter('');
        setOnlyHeld(false);
        setOnlyReview(false);
        setOnlyUndiscoverable(false);
        setIncludeDeleted(false);
        setOnlyOrphans(false);
        setOnlyVerified(false);
        setOnlyVat(false);
        break;
      case 'orphans':
        setOnlyOrphans((v) => !v);
        break;
      case 'testers':
        setTesterFilter((v) => (v === '1' ? '' : '1'));
        break;
      case 'held':
        setOnlyHeld((v) => !v);
        break;
      case 'review':
        setOnlyReview((v) => !v);
        break;
      case 'undiscoverable':
        setOnlyUndiscoverable((v) => !v);
        break;
      case 'deleted':
        setIncludeDeleted((v) => !v);
        break;
      case 'verified':
        setOnlyVerified((v) => !v);
        break;
      case 'vat':
        setOnlyVat((v) => !v);
        break;
      default:
        return;
    }
    resetPage();
  };

  const handleOrgJump = (org: AdminOrganizationSearchOrg) => {
    setOrgJumpQuery('');
    navigate(`/admin/organizations/${org.oid}`);
  };

  const submitOwnerEmailLookup = (e: React.FormEvent) => {
    e.preventDefault();
    const email = ownerEmailInput.trim();
    if (!email) return;
    setOwnerEmailLookup(email);
  };

  const toggleSort = (field: string) => {
    setSortOrder(sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc');
    setSortBy(field);
    resetPage();
  };

  const lifecycleMutation = useOrganizationLifecycleMutation();
  const [pendingAction, setPendingAction] = useState<{
    org: AdminOrganizationListRow;
    action: OrgLifecycleAction;
  } | null>(null);

  const runLifecycle = async () => {
    if (!pendingAction) return;
    const { org, action } = pendingAction;
    const copy = LIFECYCLE_COPY[action];
    const toastId = toast.loading(`${copy.label} ${org.name}…`);

    try {
      await lifecycleMutation.mutateAsync({ oid: org.oid, action });
      toast.success(`${copy.label} succeeded`, {
        id: toastId,
        description: `${org.name} was updated.`,
        richColors: true,
      });
    } catch (err: any) {
      toast.error(`${copy.label} failed`, {
        id: toastId,
        description: err?.message ?? 'Something went wrong.',
        richColors: true,
        duration: 5000,
      });
    } finally {
      setPendingAction(null);
    }
  };

  const [addOpen, setAddOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const createMutation = useCreateOrganizationMutation();

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name.trim()) return;

    const toastId = toast.loading('Creating organization…');
    try {
      await createMutation.mutateAsync(createForm);
      toast.success('Organization created', {
        id: toastId,
        description: `${createForm.name} was created. New organizations start in review.`,
        richColors: true,
      });
      setCreateForm(EMPTY_CREATE);
      setAddOpen(false);
    } catch (err: any) {
      toast.error('Failed to create organization', {
        id: toastId,
        description: err?.message ?? 'Something went wrong.',
        richColors: true,
        duration: 5000,
      });
    }
  };

  const underReview = summary?.under_review_organizations;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage organizations, membership, and moderation state
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/organizations/moderation')}
            >
              Moderation queue
              {underReview != null && underReview > 0 ? (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  {underReview}
                </span>
              ) : null}
            </Button>
            <Button onClick={() => setAddOpen(true)} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add Organization
            </Button>
          </div>
        </div>

        <StatsGrid
          stats={orgStats}
          isLoading={isLoading && !summary}
          error={error ? (error as Error).message : null}
          className="lg:grid-cols-4 xl:grid-cols-4"
          onStatClick={handleStatClick}
          activeKeys={activeStatKeys}
        />

        <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label
                htmlFor="org-filter-search"
                className="block text-xs font-medium text-gray-500"
              >
                Search
              </label>
              <div className="relative w-72">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  id="org-filter-search"
                  className="pl-9 pr-9"
                  placeholder="Name, owner, ffid, VAT…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    resetPage();
                  }}
                />
                {(isDebouncing || isFetching) && (
                  <Loader2
                    className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400"
                    aria-label="Loading"
                  />
                )}
              </div>
            </div>

            <SearchableSelect
              options={SIZE_OPTIONS}
              value={size}
              onChange={(value) => {
                setSize(value);
                resetPage();
              }}
              placeholder="Organization size"
              icon={Users2}
              className="w-full md:w-44"
            />
            <SearchableSelect
              options={LISTING_OPTIONS}
              value={listings}
              onChange={(value) => {
                setListings(value);
                resetPage();
              }}
              placeholder="Listings"
              icon={ListFilter}
              className="w-full md:w-40"
            />
            <SearchableSelect
              options={MEMBERS_OPTIONS}
              value={members}
              onChange={(value) => {
                setMembers(value);
                resetPage();
              }}
              placeholder="Members"
              icon={Users2}
              className="w-full md:w-40"
            />
            <SearchableSelect
              options={PERIOD_OPTIONS}
              value={period}
              onChange={(value) => {
                setPeriod(value);
                resetPage();
              }}
              placeholder="Created any time"
              icon={CalendarRange}
              className="w-full md:w-44"
            />

            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500">
                Jump to org
              </label>
              <OrgSearchSelect
                value={orgJumpQuery}
                onChange={setOrgJumpQuery}
                onSelect={handleOrgJump}
                className="w-72"
              />
            </div>

            <form onSubmit={submitOwnerEmailLookup} className="space-y-1">
              <label
                htmlFor="org-owner-email-lookup"
                className="block text-xs font-medium text-gray-500"
              >
                Owner email lookup
              </label>
              <div className="flex gap-2">
                <Input
                  id="org-owner-email-lookup"
                  type="email"
                  className="w-52"
                  placeholder="ada@example.com"
                  value={ownerEmailInput}
                  onChange={(e) => setOwnerEmailInput(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="outline"
                  disabled={!ownerEmailInput.trim() || ownerLookup.isFetching}
                >
                  {ownerLookup.isFetching ? 'Looking…' : 'Lookup'}
                </Button>
              </div>
            </form>

            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={onlyOrphans}
                onChange={(e) => {
                  setOnlyOrphans(e.target.checked);
                  resetPage();
                }}
              />
              No owner
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={onlyReview}
                onChange={(e) => {
                  setOnlyReview(e.target.checked);
                  resetPage();
                }}
              />
              In review
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={onlyHeld}
                onChange={(e) => {
                  setOnlyHeld(e.target.checked);
                  resetPage();
                }}
              />
              On hold
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={includeDeleted}
                onChange={(e) => {
                  setIncludeDeleted(e.target.checked);
                  resetPage();
                }}
              />
              Deleted
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={onlyUndiscoverable}
                onChange={(e) => {
                  setOnlyUndiscoverable(e.target.checked);
                  resetPage();
                }}
              />
              Undiscoverable
            </label>
            <div className="space-y-1 pb-0.5">
              <label
                htmlFor="org-tester-filter"
                className="block text-xs font-medium text-gray-500"
              >
                Test data
              </label>
              <select
                id="org-tester-filter"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={testerFilter}
                onChange={(e) => {
                  setTesterFilter(e.target.value as '' | '0' | '1');
                  resetPage();
                }}
              >
                <option value="">All (test + real)</option>
                <option value="0">Real only</option>
                <option value="1">Test only</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh
                    label="Organization"
                    field="name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onToggle={toggleSort}
                  />
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Owner
                  </th>
                  <SortableTh
                    label="Members"
                    field="members"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onToggle={toggleSort}
                  />
                  <SortableTh
                    label="Listings"
                    field="listings"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onToggle={toggleSort}
                  />
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    State
                  </th>
                  <SortableTh
                    label="Created"
                    field="created"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onToggle={toggleSort}
                  />
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading &&
                  !data &&
                  Array.from({ length: limit }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 7 }).map((__, cell) => (
                        <td key={cell} className="whitespace-nowrap px-6 py-4">
                          <Skeleton className="h-4 w-24" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!isLoading && isError && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-sm text-red-600">
                      Could not load organizations. Try again.
                    </td>
                  </tr>
                )}

                {!isLoading && !isError && organizations.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-400">
                      No organizations match the current filters.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  organizations.map((org) => (
                    <tr
                      key={org.oid}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/admin/organizations/${org.oid}`)}
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                            <Building2 className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {org.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              oid {org.oid}
                              {org.ffid ? ` · ${org.ffid}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {org.owner_uid == null && !org.owner ? (
                          <div>
                            <div className="text-sm font-medium text-amber-700">
                              No owner
                            </div>
                            <div className="text-xs text-amber-600">Orphaned org</div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-sm text-gray-900">
                              {org.owner || '—'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {org.owner_email || '—'}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/organizations/${org.oid}?tab=members`);
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                          title="View members"
                        >
                          {org.members ?? 0}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/organizations/${org.oid}?tab=listings`);
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                          title="View listings"
                        >
                          {org.listings ?? 0}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <OrgStateBadges org={org} />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {org.date_added
                          ? moment(org.date_added).format('YYYY-MM-DD')
                          : '—'}
                      </td>
                      <td
                        className="whitespace-nowrap px-6 py-4 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Organization actions"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                navigate(`/admin/organizations/${org.oid}`)
                              }
                            >
                              Manage
                            </DropdownMenuItem>
                            {org.active === 1 ? (
                              <DropdownMenuItem
                                onClick={() =>
                                  setPendingAction({ org, action: 'deactivate' })
                                }
                              >
                                Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() =>
                                  setPendingAction({ org, action: 'activate' })
                                }
                              >
                                Activate
                              </DropdownMenuItem>
                            )}
                            {org.deleted === 1 ? (
                              <DropdownMenuItem
                                onClick={() =>
                                  setPendingAction({ org, action: 'undelete' })
                                }
                              >
                                Undelete
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() =>
                                  setPendingAction({ org, action: 'delete' })
                                }
                              >
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <Pagination
              total={data?.total_organizations ?? 0}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={setPage}
              setItemsPerPage={(next: number) => {
                setLimit(next);
                resetPage();
              }}
              showButtons
              showSelect
              selectOptions={[10, 20, 30, 40, 50]}
            />
            {isFetching && !isLoading && (
              <p className="text-xs text-gray-400 mt-1">Refreshing…</p>
            )}
          </div>
        </div>
      </div>

      <AlertDialog
        open={Boolean(pendingAction)}
        onOpenChange={(next) => {
          if (!next) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction ? LIFECYCLE_COPY[pendingAction.action].title : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction ? (
                <>
                  <span className="font-medium">{pendingAction.org.name}</span> —{' '}
                  {LIFECYCLE_COPY[pendingAction.action].body}
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                pendingAction && LIFECYCLE_COPY[pendingAction.action].destructive
                  ? 'bg-red-600 hover:bg-red-700'
                  : undefined
              }
              onClick={(e) => {
                e.preventDefault();
                void runLifecycle();
              }}
              disabled={lifecycleMutation.isPending}
            >
              {lifecycleMutation.isPending
                ? 'Working…'
                : pendingAction
                  ? LIFECYCLE_COPY[pendingAction.action].label
                  : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={addOpen}
        onOpenChange={(next) => {
          if (!next) {
            setCreateForm(EMPTY_CREATE);
            setAddOpen(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Add Organization
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="org-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="org-name"
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="org-description">Description</Label>
              <Input
                id="org-description"
                value={createForm.description ?? ''}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="org-country">Country</Label>
                <Input
                  id="org-country"
                  placeholder="GH"
                  value={createForm.country ?? ''}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      country: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="org-locale">Locale</Label>
                <Input
                  id="org-locale"
                  placeholder="en_GB"
                  value={createForm.locale ?? ''}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      locale: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <p className="text-xs text-gray-500">
              A new organization is created awaiting name moderation, so it will
              not be publicly listable until approved in the moderation queue.
            </p>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateForm(EMPTY_CREATE);
                  setAddOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || !createForm.name.trim()}
              >
                {createMutation.isPending ? 'Creating…' : 'Create Organization'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationListPage;
